import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { Plus, Pencil, Archive, User, Building, Sparkles, Save, Rocket, MapPin, ExternalLink } from "lucide-react";

/**
 * Ad-hoc Requesters — light contact records for walk-in / one-off temp
 * job customers (weddings, film shoots, event venues). See Phase 1 of
 * the Temp Jobs feature in /app/memory/PRD.md.
 *
 * These records **never** get a portal login. They exist so temp sites
 * can point at something other than a client, and so ops has a
 * lightweight place to record the phone number for the wedding
 * coordinator. `promoted_to_client_id` is populated by the Phase-3
 * "Promote to Client" flow.
 */
const empty = {
  name: "", kind: "business", contact_name: "", phone: "", email: "", notes: "", status: "active",
};

export default function AdhocRequesters() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  const [search, setSearch] = useState("");
  // Phase 3 — Promote-to-Client modal
  const [promoteFor, setPromoteFor] = useState(null);
  const [promoteForm, setPromoteForm] = useState(null);
  const [promoteBusy, setPromoteBusy] = useState(false);
  // iter303 — Full temp-site inventory. Previously this page listed
  // only ad-hoc requesters (the walk-in contacts); temp sites tied to
  // EXISTING clients were nowhere to be seen. Now we surface every
  // `is_temporary: true` site regardless of whether it's linked to a
  // client_id or an adhoc_requester_id, so ops has a single source of
  // truth for "what temp jobs do we have running?"
  const [tempSites, setTempSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [tempSiteFilter, setTempSiteFilter] = useState("active"); // active | archived | all

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      // iter303 — fetch requesters + sites + clients in parallel so the
      // Temp Sites table shows names, client links, and end dates without
      // a follow-up round-trip.
      const [reqRes, sitesRes, clientsRes] = await Promise.all([
        api.get("/adhoc-requesters", { params }),
        api.get("/sites", { params: { include_temporary: true } }).catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setRows(reqRes.data || []);
      const allSites = Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data?.items || []);
      setTempSites(allSites.filter((s) => s.is_temporary));
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data?.items || []));
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load ad-hoc requesters"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setEditing(null); setForm(empty); setOpenForm(true); };
  const startEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); setOpenForm(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/adhoc-requesters/${editing.id}`, form);
        toast.success("Requester updated");
      } else {
        await api.post("/adhoc-requesters", form);
        toast.success("Requester created");
      }
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (row) => {
    if (!confirm(`Archive "${row.name}"? Sites linked to this requester must be closed first.`)) return;
    try {
      await api.delete(`/adhoc-requesters/${row.id}`);
      toast.success("Requester archived");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Archive failed"));
    }
  };

  // ── Promote-to-Client (Phase 3) ────────────────────────────
  // Opens a preview modal seeded with the requester's data so the admin
  // can review + tweak before atomically creating the client and
  // reassigning every linked temp site.
  const startPromote = async (row) => {
    setPromoteFor(row);
    // Pre-fill defaults: seed address from linked site if we can find one.
    let seedAddress = "";
    try {
      const sites = await api.get("/sites", { params: { include_temporary: true } });
      const linked = (sites.data || []).filter((s) => s.adhoc_requester_id === row.id);
      const primary = linked.find((s) => s.status !== "ended") || linked[0];
      if (primary?.address) seedAddress = primary.address;
    } catch { /* ignore — form will require manual entry */ }
    setPromoteForm({
      company_name:  row.name || "",
      address:       seedAddress,
      contact_person: row.contact_name || "",
      contact_phone: row.phone || "",
      contact_email: row.email || "",
      notes:         row.notes || "",
      demote_sites_to_permanent: true,
    });
  };

  const submitPromote = async () => {
    if (!promoteForm.company_name.trim()) { toast.error("Company name is required"); return; }
    if (!promoteForm.address.trim()) { toast.error("Address is required"); return; }
    setPromoteBusy(true);
    try {
      const res = await api.post(`/adhoc-requesters/${promoteFor.id}/promote-to-client`, promoteForm);
      toast.success(`Promoted → ${res.data.client_name} · ${res.data.sites_reassigned} site${res.data.sites_reassigned === 1 ? "" : "s"} reassigned`);
      setPromoteFor(null);
      setPromoteForm(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Promote failed"));
    } finally {
      setPromoteBusy(false);
    }
  };

  // iter303 — Client / requester name resolver + status filter for the
  // Temp Sites table.
  const clientNameById = useMemo(() => {
    const m = new Map();
    for (const c of clients) m.set(c.id, c.company_name || c.name);
    return m;
  }, [clients]);
  const requesterNameById = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.id, r.name);
    return m;
  }, [rows]);
  const filteredTempSites = useMemo(() => {
    let arr = tempSites;
    if (tempSiteFilter !== "all") {
      arr = arr.filter((s) => {
        const st = s.status || "active";
        return tempSiteFilter === "archived" ? st === "ended" : st !== "ended";
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((s) => (s.name || "").toLowerCase().includes(q)
        || (clientNameById.get(s.client_id) || "").toLowerCase().includes(q)
        || (requesterNameById.get(s.adhoc_requester_id) || "").toLowerCase().includes(q));
    }
    // Sort: soonest end-date first, then by name.
    return [...arr].sort((a, b) => {
      const ae = a.temp_end_date || "9999";
      const be = b.temp_end_date || "9999";
      if (ae !== be) return ae.localeCompare(be);
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [tempSites, tempSiteFilter, search, clientNameById, requesterNameById]);

  const daysUntil = (isoDate) => {
    if (!isoDate) return null;
    const end = new Date(isoDate + "T23:59:59");
    const now = new Date();
    return Math.ceil((end - now) / 86400000);
  };

  return (
    <div className="space-y-6" data-testid="adhoc-requesters-page">
      <PageHeader
        title="Temp WO Sites"
        subtitle="Every temporary work-order site in one place — walk-in requesters (weddings, film shoots) plus temp sites tied to existing clients. Sites auto-archive 48h after their end date."
        action={
          <ActionButton data-testid="new-adhoc-btn" onClick={startNew}>
            <Plus className="size-4" /> New Requester
          </ActionButton>
        }
      />

      {/* ── Temp Sites (iter303) ──────────────────────────────────
          Combined list of every `is_temporary: true` site so ops
          doesn't need to hop back to Sites to see temp jobs booked
          under existing clients. */}
      <section data-testid="temp-sites-section" className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-200 flex items-center gap-2">
              <MapPin className="size-4 text-[#FF9F0A]" /> Temp Sites
              <span className="text-[10px] font-mono text-zinc-500">({filteredTempSites.length})</span>
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Includes temp jobs for walk-in requesters AND existing clients.
            </p>
          </div>
          <div className="flex items-center gap-1 border border-[#27272A]" data-testid="temp-site-status-filter">
            {["active", "archived", "all"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setTempSiteFilter(s)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                  tempSiteFilter === s ? "bg-[#FF9F0A] text-black" : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`temp-site-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="temp-sites-list">
          {loading ? (
            <div className="p-8 text-center text-xs text-zinc-500">Loading…</div>
          ) : filteredTempSites.length === 0 ? (
            <div className="p-8 text-center text-xs text-zinc-500">
              No temp sites {tempSiteFilter === "active" ? "currently active" : "match this filter"}. Create one from
              <Link to="/sites" className="text-[#0A84FF] hover:text-white ml-1">the Sites page</Link>
              {" "}and toggle
              <span className="text-zinc-300"> Temp job</span>.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
                <tr>
                  <th className="text-left px-4 py-3">Site</th>
                  <th className="text-left px-4 py-3">Belongs to</th>
                  <th className="text-left px-4 py-3">End Date</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTempSites.map((s) => {
                  const clientName = s.client_id ? clientNameById.get(s.client_id) : null;
                  const requesterName = s.adhoc_requester_id ? requesterNameById.get(s.adhoc_requester_id) : null;
                  const days = daysUntil(s.temp_end_date);
                  const isEnded = (s.status || "active") === "ended";
                  const tone = isEnded ? "muted" : (days !== null && days <= 3 ? "red" : (days !== null && days <= 7 ? "amber" : "green"));
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-[#1c1c1e] hover:bg-[#121212]"
                      data-testid={`temp-site-row-${s.id}`}
                    >
                      <td className="px-4 py-3 text-zinc-100 font-medium">
                        {s.name}
                        {s.address && (
                          <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate max-w-[280px]">
                            {s.address}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        {clientName ? (
                          <span className="inline-flex items-center gap-1">
                            <Building className="size-3" /> {clientName}
                            <span className="text-[9px] uppercase tracking-widest text-[#0A84FF] ml-1">CLIENT</span>
                          </span>
                        ) : requesterName ? (
                          <span className="inline-flex items-center gap-1">
                            <User className="size-3" /> {requesterName}
                            <span className="text-[9px] uppercase tracking-widest text-[#FF9F0A] ml-1">WALK-IN</span>
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-400 font-mono text-xs">
                        {s.temp_end_date || <span className="text-zinc-600">—</span>}
                        {s.temp_end_date && !isEnded && (
                          <div className={`text-[10px] mt-0.5 ${days < 0 ? "text-[#FF3B30]" : days <= 3 ? "text-[#FF3B30]" : days <= 7 ? "text-[#FF9F0A]" : "text-zinc-500"}`}>
                            {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "ends today" : `${days}d left`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <PillBadge tone={tone}>{s.status || "active"}</PillBadge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/sites?site=${encodeURIComponent(s.id)}`}
                          data-testid={`temp-site-edit-${s.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#0A84FF] hover:text-white"
                        >
                          <ExternalLink className="size-3" /> Edit in Sites
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ── Requesters section ─────────────────────────────────── */}
      <section className="space-y-3 pt-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-200 flex items-center gap-2">
            <User className="size-4 text-[#0A84FF]" /> Walk-in Requesters
            <span className="text-[10px] font-mono text-zinc-500">({rows.length})</span>
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Light contact records for one-off customers who don&apos;t need portal access.
          </p>
        </div>

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 border border-[#27272A]" data-testid="adhoc-status-filter">
          {["active", "archived", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                statusFilter === s ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}
              data-testid={`adhoc-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search name / contact / phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputClass} w-72`}
          data-testid="adhoc-search-input"
        />
      </div>

      {/* ── Table ──────────────────────────────────────────────── */}
      <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="adhoc-list">
        {loading ? (
          <div className="p-8 text-center text-xs text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-xs text-zinc-500">
            No ad-hoc requesters yet. Click <span className="text-zinc-300">New Requester</span> to add your first walk-in.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#1c1c1e] hover:bg-[#121212]" data-testid={`adhoc-row-${r.id}`}>
                  <td className="px-4 py-3 text-zinc-100 font-medium">
                    {r.name}
                    {r.promoted_to_client_id && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest text-[#0A84FF]" title="Promoted to a full client">
                        <Sparkles className="size-2.5 inline mr-0.5" /> promoted
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">
                    {r.kind === "individual" ? (
                      <><User className="size-3 inline mr-1" />Individual</>
                    ) : (
                      <><Building className="size-3 inline mr-1" />Business</>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{r.contact_name || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{r.phone || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3">
                    <PillBadge tone={r.status === "archived" ? "muted" : "green"}>{r.status}</PillBadge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <GhostButton onClick={() => startEdit(r)} data-testid={`adhoc-edit-${r.id}`}>
                      <Pencil className="size-3" /> Edit
                    </GhostButton>
                    {r.status !== "archived" && !r.promoted_to_client_id && (
                      <GhostButton onClick={() => startPromote(r)} data-testid={`adhoc-promote-${r.id}`}>
                        <Rocket className="size-3" /> Promote
                      </GhostButton>
                    )}
                    {r.status !== "archived" && (
                      <GhostButton onClick={() => archive(r)} data-testid={`adhoc-archive-${r.id}`}>
                        <Archive className="size-3" /> Archive
                      </GhostButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </section>

      {/* ── Form modal ─────────────────────────────────────────── */}
      {openForm && (
        <Modal
          title={editing ? "Edit Requester" : "New Ad-hoc Requester"}
          onClose={() => setOpenForm(false)}
          testId="adhoc-form-modal"
        >
          <div className="space-y-4">
            <FormField label="Name *">
              <input
                autoFocus
                className={inputClass}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Smith Wedding · Riverside Film Shoot"
                data-testid="adhoc-name-input"
              />
            </FormField>
            <FormField label="Kind">
              <div className="grid grid-cols-2 gap-2">
                {[["business", "Business"], ["individual", "Individual"]].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set("kind", v)}
                    className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${
                      form.kind === v ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10" : "border-[#27272A] text-zinc-500 hover:text-zinc-300"
                    }`}
                    data-testid={`adhoc-kind-${v}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Contact Name">
                <input
                  className={inputClass}
                  value={form.contact_name}
                  onChange={(e) => set("contact_name", e.target.value)}
                  data-testid="adhoc-contact-input"
                />
              </FormField>
              <FormField label="Phone">
                <input
                  className={inputClass}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  data-testid="adhoc-phone-input"
                />
              </FormField>
            </div>
            <FormField label="Email">
              <input
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="adhoc-email-input"
              />
            </FormField>
            <FormField label="Notes">
              <textarea
                rows={3}
                className={inputClass}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Optional — internal notes, invoicing reminders, gate codes, etc."
                data-testid="adhoc-notes-input"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setOpenForm(false)} disabled={busy}>Cancel</GhostButton>
              <ActionButton onClick={submit} disabled={busy} data-testid="adhoc-save-btn">
                <Save className="size-4" /> {busy ? "Saving…" : editing ? "Update" : "Create"}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Promote-to-Client modal (Phase 3) ──────────────────── */}
      {promoteFor && promoteForm && (
        <Modal
          title={`Promote "${promoteFor.name}" to Client`}
          onClose={() => { setPromoteFor(null); setPromoteForm(null); }}
          testId="adhoc-promote-modal"
        >
          <div className="space-y-4">
            <div className="border border-[#0A84FF] bg-[#0A84FF]/[0.06] p-3 text-xs text-zinc-300">
              <div className="flex items-start gap-2">
                <Sparkles className="size-4 text-[#0A84FF] mt-0.5 flex-shrink-0" />
                <div>
                  This creates a full <span className="text-zinc-100 font-medium">Client</span> record, moves every site currently linked to this requester over to the new client, and archives the requester.
                  <span className="block mt-1 text-zinc-500">
                    The requester itself stays in the archive with a forward-pointer for audit.
                  </span>
                </div>
              </div>
            </div>

            <FormField label="Company Name *">
              <input
                autoFocus
                className={inputClass}
                value={promoteForm.company_name}
                onChange={(e) => setPromoteForm((f) => ({ ...f, company_name: e.target.value }))}
                data-testid="promote-company-name"
              />
            </FormField>
            <FormField label="Address *">
              <input
                className={inputClass}
                value={promoteForm.address}
                onChange={(e) => setPromoteForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Seeded from the requester's first linked site — edit if you want a different HQ address"
                data-testid="promote-address"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Contact Person">
                <input
                  className={inputClass}
                  value={promoteForm.contact_person}
                  onChange={(e) => setPromoteForm((f) => ({ ...f, contact_person: e.target.value }))}
                  data-testid="promote-contact-person"
                />
              </FormField>
              <FormField label="Contact Phone">
                <input
                  className={inputClass}
                  value={promoteForm.contact_phone}
                  onChange={(e) => setPromoteForm((f) => ({ ...f, contact_phone: e.target.value }))}
                  data-testid="promote-contact-phone"
                />
              </FormField>
            </div>
            <FormField label="Contact Email">
              <input
                type="email"
                className={inputClass}
                value={promoteForm.contact_email}
                onChange={(e) => setPromoteForm((f) => ({ ...f, contact_email: e.target.value }))}
                data-testid="promote-contact-email"
              />
            </FormField>
            <FormField label="Notes">
              <textarea
                rows={2}
                className={inputClass}
                value={promoteForm.notes}
                onChange={(e) => setPromoteForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="promote-notes"
              />
            </FormField>
            <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#FF3B30] size-4"
                  checked={promoteForm.demote_sites_to_permanent}
                  onChange={(e) => setPromoteForm((f) => ({ ...f, demote_sites_to_permanent: e.target.checked }))}
                  data-testid="promote-demote-sites"
                />
                <div>
                  <div className="text-sm text-zinc-100">Convert linked sites to permanent</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    Recommended — clears <span className="font-mono">is_temporary</span> + end date on every site. Uncheck to keep them as temp jobs under the new client.
                  </div>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => { setPromoteFor(null); setPromoteForm(null); }} disabled={promoteBusy}>Cancel</GhostButton>
              <ActionButton onClick={submitPromote} disabled={promoteBusy} data-testid="promote-confirm-btn">
                <Rocket className="size-4" /> {promoteBusy ? "Promoting…" : "Promote"}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
